import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CommonResponse, PaginatedRequest } from '../models';
import { Career } from '../models/career/career';
import { environment } from '../../../environments/environment';
import { CurrentUniversityService } from './current-university.service';
import { CurrentCareerService } from './current-career.service';
import { AuthenticationService } from './authentication.service';
import { RoleType } from '../enums/role-type';
import { TutorCareerService } from './university/tutor-career.service';
import { DirectorService } from './school/director.service';
import { OperationalTutorService } from './university/operational-tutor.service';
import { DidacticProgramTotalizer, Totalizer } from '../models/totalizer/totalizer';

export type CareerListRequest = PaginatedRequest<{
	status?: string;
	cohort_id?: string;
	school_university_id?: string;
	columns?: Record<string, any>;
}>;

@Injectable({
	providedIn: 'root',
})
export class CareerService {
	public careersFilteredBySchoolAndAuthUser: BehaviorSubject<Array<Career> | null>;

	constructor(
		private _http: HttpClient,
		private _currentUniversityService: CurrentUniversityService,
		private _currentCareer: CurrentCareerService,
		private authService: AuthenticationService,
		private tutorService: TutorCareerService,
		private directorService: DirectorService,
		private operationalTutor: OperationalTutorService
	) {
		this.careersFilteredBySchoolAndAuthUser = new BehaviorSubject<Array<Career> | null>(null);
		this.checkCareer();

		if (this._currentCareer.getCareerFromStorage() && this.authService.activeProfileId.value === RoleType.Trainee) {
			this.getCareersFilteredByAuthUser().subscribe(res => {
				console.log('Careers filtered by auth user:', res.data);
				this.careersFilteredBySchoolAndAuthUser.next(res.data);
			});
		}
	}

	checkCareer() {
		if (this._currentCareer.getCareerFromStorage()) {
			this._currentCareer.currentCareerId.next(this._currentCareer.getCareerFromStorage());
			this.showCareer().subscribe(res => {
				this._currentCareer.currentCareer.next(res.data);
			});
		}
	}

	refreshCurrentCareer() {
		this.checkCareer();
	}

	insertCareer(
		careerData: [
			{
				profile_user_university_school_university_id?: string;
				cohort_id?: string;
				didactic_program_id?: string;
				career_id?: string;
				student_identification_number: number;
				contract_start_date: string;
			},
		]
	): Observable<CommonResponse<Career>> {
		return this._http.post<CommonResponse<Career>>('/university/careers', careerData);
	}

	showCareerUniversity(id: string): Observable<CommonResponse<Career>> {
		return this._http.get<CommonResponse<Career>>(`/university/careers/${id}`);
	}

	editCareer(
		id: string,
		careerData: [
			{
				student_identification_number: number;
				contract_start_date: string;
				close: boolean;
				logbook_closed: boolean;
			},
		]
	): Observable<CommonResponse<Career>> {
		return this._http.put<CommonResponse<Career>>(`/university/careers/${id}`, careerData);
	}

	showSchoolCareer(school_id: string, profile_user_id: string): Observable<CommonResponse<Array<Career>>> {
		return this._http.post<CommonResponse<Array<Career>>>(`/university/careers/connectable`, {
			school_id: school_id,
			profile_user_id: profile_user_id,
		});
	}

	getCareersFilteredByAuthUser(profileId?: string, schoolUniversityId?: string, universityId?: string): Observable<CommonResponse<any>> {
		if (profileId && schoolUniversityId) {
			return this._http.get<CommonResponse<any>>(`${environment.apiSchoolUrl}/careers/filtered_by/auth_user`, {
				headers: {
					Authorization: `Bearer ${sessionStorage.getItem('currentUser')}`,
					'X-Profile': profileId,
					'X-University': universityId ?? this._currentUniversityService.currentUniversity?.id ?? '',
					'X-SchoolUniversity': schoolUniversityId,
				},
			});
		} else {
			return this._http.get<CommonResponse<any>>(`${environment.apiSchoolUrl}/careers/filtered_by/auth_user`);
		}
	}

	showCareer(schoolUniversityId?: string): Observable<CommonResponse<Career>> {
		const headers: { [header: string]: string } = {};
		if (schoolUniversityId) {
			headers['X-SchoolUniversity'] = schoolUniversityId;
		}
		return this._http.get<CommonResponse<Career>>(`${environment.apiSchoolUrl}/career`, { headers });
	}

	careerTotalizer(
		pool: 'approved' | 'qualifying',
		careerId?: string,
		schoolUniversityId?: string
	): Observable<CommonResponse<Array<DidacticProgramTotalizer>>> {
		const headers: { [header: string]: string } = {};
		if (careerId) {
			headers['X-Career'] = careerId;
		}
		if (schoolUniversityId) {
			headers['X-SchoolUniversity'] = schoolUniversityId;
		}
		return this._http.get<CommonResponse<Array<DidacticProgramTotalizer>>>(
			`${environment.apiSchoolUrl}/career/didactic_programs_totalizers/data_pool/${pool}`,
			{ headers }
		);
	}

	careerTotalizerForUniversity(
		career_id: string,
		pool: 'approved' | 'qualifying'
	): Observable<CommonResponse<Array<DidacticProgramTotalizer>>> {
		return this._http.get<CommonResponse<Array<DidacticProgramTotalizer>>>(
			`${environment.apiUniversityUrl}/didactic_programs_totalizers/careers/${career_id}/data_pool/${pool}`
		);
	}

	getSkillTotalizersByYear(year: number): Observable<CommonResponse<any>> {
		return this._http.get<CommonResponse<any>>(
			`${environment.apiSchoolUrl}/career/log_book_details/skill_totalizers/year/${year}`
		);
	}
}
